<template>
    <div>
        <BaseTable
            :title="title"
            :columns="columns"
            :data="fields"
            :loading="loading"
            :skeleton="loading"
            :skeleton-rows="2"
            :empty-text="emptyText"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers por columna -->
            <template #skeleton-header-name>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-12"></div>
            </template>

            <template #skeleton-header-type>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-10"></div>
            </template>

            <template #skeleton-header-required>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>

            <template #skeleton-header-deportista>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>

            <template #skeleton-header-representante>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>

            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>

            <!-- Skeleton rows per columna -->
            <template #skeleton-name>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-3/4"></div>
            </template>

            <template #skeleton-type>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/4"></div>
            </template>

            <template #skeleton-required>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>

            <template #skeleton-deportista>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>

            <template #skeleton-representante>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>

            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded"></div>
            </template>

            <template #cell-name="{ value }">
                <span>{{ value }}</span>
            </template>

            <template #cell-type="{ row }">
                <span v-if="(row.type === 'select' || row.type === 'multiselect') && row.options && row.options.length > 0" class="tw:inline-flex tw:items-center tw:gap-1">
                    <span>{{ row.type }}</span>
                    <InfoToolTip :info="row.options.map(o => o.name).join(', ')" />
                </span>
                <span v-else>{{ row.type }}</span>
            </template>

            <template #cell-required="{ row }">
                <span v-if="row.required" class="tw:text-green-500">
                    <i class="fas fa-check-circle"></i>
                </span>
                <span v-else class="tw:text-red-500">
                    <i class="fas fa-times-circle"></i>
                </span>
            </template>

            <template #cell-deportista="{ row }">
                <span v-if="row.deportista" class="tw:text-green-500">
                    <i class="fas fa-check-circle"></i>
                </span>
                <span v-else class="tw:text-red-500">
                    <i class="fas fa-times-circle"></i>
                </span>
            </template>

            <template #cell-representante="{ row }">
                <span v-if="row.representante" class="tw:text-green-500">
                    <i class="fas fa-check-circle"></i>
                </span>
                <span v-else class="tw:text-red-500">
                    <i class="fas fa-times-circle"></i>
                </span>
            </template>

            <template #cell-opciones="{ row }">
                <OptionsDropdownComponent
                    :options="[
                        { type: 'action', action: () => showDelete(row.id), label: 'Borrar' }
                    ]"
                />
            </template>
        </BaseTable>
        <!-- El modal debe vivir FUERA de BaseTable: BaseTable no renderiza
        slot default y cualquier hijo directo se descarta en silencio
        (el data-cy="confirmar-eliminar" nunca existia en el DOM). -->
        <BorrarModalComponent
            v-if="fieldToDelete !== null"
            name="desvincular-campo"
            title="Desvincular campo"
            @confirm="handleConfirmDelete"
        >
            ¿Está segur@ de desvincular el campo?
        </BorrarModalComponent>
    </div>
</template>

<script setup>
    import { getCurrentInstance, nextTick, ref } from 'vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue';
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue';
    import InfoToolTip from '@/modules/ui/InfoToolTip.vue';

    const props = defineProps({
        fields: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Array,
            required: true
        },
        emptyText: {
            type: String,
            default: 'No hay campos personalizados'
        },
        title: {
            type: String,
            default: ''
        },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    });

    const emit = defineEmits(['delete']);

    const { proxy } = getCurrentInstance();

    const fieldToDelete = ref(null);

    function showDelete(fieldId) {
        fieldToDelete.value = fieldId;
        // nextTick espera a que Vue monte el modal (v-if)
        // antes de llamar a $modal.show().
        nextTick(() => {
            proxy.$modal.show('desvincular-campo');
        });
    }

    function handleConfirmDelete() {
        emit('delete', fieldToDelete.value);
        proxy.$modal.hide('desvincular-campo');
        fieldToDelete.value = null;
    }
</script>
